import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Loader2, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  type FamilyHubResponse,
  useGetFamilyHub,
} from '~entities/family';

import { AddStudentDialog } from '~features/students/ui/add-student-dialog';
import { FamilyPaymentOverviewCard } from '~features/students/ui/family-payment-overview-card';

import { KEYS } from '~shared/api/query-keys';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

const resolveAddStudentParent = (
  data: FamilyHubResponse,
): { parentId: string; label: string } | null => {
  const billingId = data.family.billingParentId?.trim();
  const billingContact = billingId
    ? data.contacts.find((c) => c.parentId === billingId)
    : undefined;
  const primaryContact =
    data.contacts.find((c) => c.relationship === 'Primary') ??
    data.contacts[0];
  const contact = billingContact ?? primaryContact;
  if (!contact) return null;

  const label =
    `${contact.parent?.firstName ?? ''} ${contact.parent?.lastName ?? ''}`.trim() ||
    contact.parent?.name?.trim() ||
    'Parent';

  return { parentId: contact.parentId, label };
};

export const FamilyHubSummary = ({ familyId }: { familyId: string }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useGetFamilyHub(familyId);
  const [addStudentOpen, setAddStudentOpen] = useState(false);

  const addStudentParent = useMemo(
    () => (data ? resolveAddStudentParent(data) : null),
    [data],
  );

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading summary…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className='text-sm text-destructive'>Could not load family summary.</p>
    );
  }

  const billingParentId = data.family.billingParentId?.trim();

  return (
    <div className='space-y-4'>
      {billingParentId ? (
        <FamilyPaymentOverviewCard parentId={billingParentId} />
      ) : null}

      <div className='grid gap-4 lg:grid-cols-2'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0'>
            <CardTitle className='text-lg'>Students</CardTitle>
            {addStudentParent ? (
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  setAddStudentOpen(true);
                }}
              >
                <Plus className='size-4' />
                Add student
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className='space-y-1'>
            {data.students.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No students are linked to this family yet.
              </p>
            ) : (
              data.students.map((student) => (
                <div
                  key={student._id}
                  className='flex items-center gap-2 rounded-md border border-transparent px-2 py-2 transition-colors hover:border-border hover:bg-muted/40'
                >
                  <Link
                    to='/students/$studentId/details'
                    params={{ studentId: student._id }}
                    className='min-w-0 flex-1'
                  >
                    <p className='font-medium'>{student.name ?? 'Student'}</p>
                    {student.dob ? (
                      <p className='text-xs text-muted-foreground'>
                        {formatDateOnlySafe(student.dob, 'PPP')}
                      </p>
                    ) : null}
                  </Link>
                  <Button
                    asChild
                    size='sm'
                    variant='ghost'
                    title='Edit student'
                  >
                    <Link
                      to='/students/$studentId/details'
                      params={{ studentId: student._id }}
                      search={{ edit: true }}
                      aria-label={`Edit ${student.name ?? 'student'}`}
                    >
                      <Pencil className='size-4' />
                    </Link>
                  </Button>
                  <ChevronRight
                    className='size-4 shrink-0 text-muted-foreground'
                    aria-hidden
                  />
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Guardians</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {data.contacts.map((contact) => {
              const name =
                `${contact.parent?.firstName ?? ''} ${contact.parent?.lastName ?? ''}`.trim() ||
                contact.parent?.name ||
                contact.parentId;
              return (
                <div
                  key={contact.parentId}
                  className='flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3 last:border-b-0 last:pb-0'
                >
                  <div>
                    <p className='font-medium'>{name}</p>
                    <p className='text-xs text-muted-foreground'>
                      {contact.relationship ?? 'Guardian'}
                      {contact.isBillingContact ? ' · Billing' : ''}
                    </p>
                  </div>
                </div>
              );
            })}
            <Button asChild size='sm' variant='secondary'>
              <Link
                to='/families/$familyId/contacts'
                params={{ familyId }}
              >
                Manage guardians
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {addStudentParent ? (
        <AddStudentDialog
          open={addStudentOpen}
          onOpenChange={setAddStudentOpen}
          defaultParentId={addStudentParent.parentId}
          lockParent
          lockedParentLabel={addStudentParent.label}
          onCreated={() => {
            void queryClient.invalidateQueries({
              queryKey: [KEYS.FAMILY_HUB, familyId],
            });
          }}
        />
      ) : null}
    </div>
  );
};
